import { ExternalLinkIcon, LinkIcon, LocationIcon } from '@/assets/icons'
import { EVENT_CATEGORY_LABELS, DEFAULT_EVENT_CATEGORY } from '@/constants/eventCategories'
import { appleMaps, backgroundOrange, danger, googleBlue, instagram, mutedGrey, textMuted, tiktok } from '@/theme/colors'
import { iconSizes } from '@/theme/tokens'
import { AttachmentSection } from '@components/Attachments'
import Modal from '@components/Modal'
import { formatTimeOfDay } from '@lib/datetime'
import { Event } from '@types'
import React from 'react'
import { useParams } from 'react-router-dom'
import {
	Container,
	EmptyValue,
	FieldHeader,
	FieldLabel,
	FieldRow,
	FieldValue,
	LinkArrow,
	LinkInfo,
	LinkListReadOnly,
	LinkPreview,
	LinkSource,
	LinkThumbnail,
	LinkUrl,
	LocationDisplay,
	LocationSecondary,
	StaticLinkPreview,
	Title,
} from './EventDetailsModal.styles'

const DescriptionGlyph: React.FC = () => (
	<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
		<line x1="4" y1="7" x2="20" y2="7" />
		<line x1="4" y1="12" x2="20" y2="12" />
		<line x1="4" y1="17" x2="14" y2="17" />
	</svg>
)

const getSourceColor = (source: string) => {
	switch (source.toLowerCase()) {
		case 'tiktok':
			return tiktok
		case 'instagram':
			return instagram
		case 'youtube':
			return danger
		case 'googlemaps':
			return googleBlue
		case 'applemaps':
			return appleMaps
		default:
			return textMuted
	}
}

/**
 * The platform's name, shown in place of the address when the link came from a
 * recognisable platform: its URL is noise. A plain website or free text shows
 * its address instead, which says everything already.
 */
const formatSourceName = (source: string): string | null => {
	switch (source) {
		case 'GoogleMaps':
			return 'Google Maps'
		case 'AppleMaps':
			return 'Apple Maps'
		case 'TikTok':
		case 'Instagram':
		case 'YouTube':
			return source
		default:
			return null
	}
}

interface EventDetailsModalProps {
	isOpen: boolean
	onClose: () => void
	/** Opens the edit-event flow for this event. */
	onEdit: () => void
	onDelete?: () => void
	event: Event
}

/**
 * An event's details, read-only, as iOS and Android show them: its name,
 * category and start time, then its description, location, links and
 * attachments. The header's pencil opens the edit-event flow; the trash beside
 * it deletes.
 */
const EventDetailsModal: React.FC<EventDetailsModalProps> = ({ isOpen, onClose, onEdit, onDelete, event }) => {
	const { tripId } = useParams<{ tripId: string }>()

	const description = event.description?.trim()
	const startTime = formatTimeOfDay(event.startTime)
	const hasLinks = !!event.tripEventLinks?.length
	const hasLocation = !!event.location?.primaryText

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Event Details"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			onEdit={onEdit}
			onDelete={onDelete}
		>
			<Container>
				<Title>{event.name}</Title>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>Category</FieldLabel>
					</FieldHeader>
					<FieldValue>{EVENT_CATEGORY_LABELS[event.category || DEFAULT_EVENT_CATEGORY]}</FieldValue>
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>Start Time</FieldLabel>
					</FieldHeader>
					{startTime ? <FieldValue>{startTime}</FieldValue> : <EmptyValue>No start time</EmptyValue>}
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>
							<DescriptionGlyph />
							Description
						</FieldLabel>
					</FieldHeader>
					{description ? (
						<FieldValue>{description}</FieldValue>
					) : (
						<EmptyValue>No description</EmptyValue>
					)}
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>
							<LocationIcon />
							Location
						</FieldLabel>
					</FieldHeader>
					{hasLocation ? (
						<>
							<LocationDisplay
								href={
									event.location?.googlePlaceId
										? `https://www.google.com/maps/place/?q=place_id:${event.location.googlePlaceId}`
										: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location?.primaryText || '')}`
								}
								target="_blank"
								rel="noopener noreferrer"
							>
								{event.location?.primaryText}
							</LocationDisplay>
							{event.location?.secondaryText && (
								<LocationSecondary>{event.location.secondaryText}</LocationSecondary>
							)}
						</>
					) : (
						<EmptyValue>No location</EmptyValue>
					)}
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>
							<LinkIcon />
							Links
						</FieldLabel>
					</FieldHeader>
					{hasLinks ? (
						<LinkListReadOnly>
							{event.tripEventLinks?.map(link => {
								const hasProtocol =
									link.url.startsWith('http://') || link.url.startsWith('https://')
								const looksLikeUrl = link.url.includes('.') && !link.url.includes(' ')
								const hrefUrl = !hasProtocol && looksLikeUrl ? `https://${link.url}` : link.url
								const isClickable = hasProtocol || looksLikeUrl
								const sourceName = formatSourceName(link.source)
								const content = (
									<>
										<LinkThumbnail $src={link.thumbnail || undefined}>
											{!link.thumbnail && (
												<LinkIcon sx={{ fontSize: iconSizes.sm }} htmlColor={mutedGrey} />
											)}
										</LinkThumbnail>
										<LinkInfo>
											{sourceName ? (
												<LinkSource $color={getSourceColor(link.source)}>{sourceName}</LinkSource>
											) : (
												<LinkUrl>{link.url}</LinkUrl>
											)}
										</LinkInfo>
										<LinkArrow>
											<ExternalLinkIcon sx={{ fontSize: iconSizes.sm }} />
										</LinkArrow>
									</>
								)
								return isClickable ? (
									<LinkPreview
										key={link.id}
										href={hrefUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										{content}
									</LinkPreview>
								) : (
									<StaticLinkPreview key={link.id} as="div">
										{content}
									</StaticLinkPreview>
								)
							})}
						</LinkListReadOnly>
					) : (
						<EmptyValue>No links</EmptyValue>
					)}
				</FieldRow>

				{tripId && (
					<AttachmentSection
						tripId={tripId}
						resourceType="TRIP_EVENT"
						resourceId={event.id}
						fileIds={event.fileIds || []}
						readOnly
					/>
				)}
			</Container>
		</Modal>
	)
}

export default EventDetailsModal
